/**
 * Uploaded files section component for citations
 */

import { useTranslation } from "react-i18next"

import type { ExtraDataItem } from "../../hooks/use-citation"
import type { CategorizedCitation } from "../../utils/citation-categorizer"
import type { Document } from "@/api/models"
import type { TFunction } from "i18next"

import { getCitationFilePath } from "@/api/api"
import { FileText } from "@/components/icons"

import { DocumentCitationItem } from "../document-citation-item"

type UploadedFilesSectionProps = {
  files: CategorizedCitation[]
  documentsList: [string, Document][]
  citationSection: string
  getDocumentExtraData: (
    documents: [string, Document][],
    citation: string
  ) => ExtraDataItem[] | null
  formatCitationDisplay: (citation: string) => {
    fileNameWithPage: string
    fileNameWithoutPage: string
    pageNumber: number | null
  }
  handleDownloadClicked: (citation: string) => void
  setExtraData: (data: ExtraDataItem[] | null) => void
  setOpenMetadataDialog: (open: boolean) => void
  openPreview: (citation: string, citationSection: string, options?: { chunkText?: string }) => void
  t: TFunction
  highlightedIndex: number | null
  messageIndex?: number
  onNavigateToInline: (index: number) => void
}

export function UploadedFilesSection({
  files,
  documentsList,
  citationSection,
  getDocumentExtraData,
  formatCitationDisplay,
  handleDownloadClicked,
  setExtraData,
  setOpenMetadataDialog,
  openPreview,
  t,
  highlightedIndex,
  messageIndex,
  onNavigateToInline,
}: UploadedFilesSectionProps) {
  const { t: translate } = useTranslation()

  if (files.length === 0) return null

  return (
    <div className="space-y-2">
      <h4 className="text-muted-foreground flex items-center gap-2 text-sm font-semibold">
        <FileText className="size-4" />
        <span>
          {translate("answer.uploaded_files")} ({files.length})
        </span>
      </h4>
      <div className="grid grid-cols-1 gap-2">
        {files.map(({ citation, index }) => {
          const pathWithPage = getCitationFilePath(citation, citationSection)
          const extraData = getDocumentExtraData(documentsList, citation)

          return (
            <DocumentCitationItem
              key={index}
              disableDownload
              disableMetadata
              disablePreview
              citation={citation}
              citationSection={citationSection}
              extraData={extraData}
              formatCitationDisplay={formatCitationDisplay}
              handleDownloadClicked={handleDownloadClicked}
              index={index}
              isHighlighted={highlightedIndex === index}
              messageIndex={messageIndex}
              pathWithPage={pathWithPage}
              setExtraData={setExtraData}
              setOpenMetadataDialog={setOpenMetadataDialog}
              t={t}
              onNavigateToInline={onNavigateToInline}
              onViewDocument={openPreview}
            />
          )
        })}
      </div>
    </div>
  )
}
